import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  XMarkIcon,
  DocumentTextIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { reportTemplatesService } from '../../services/reports/report-templates';
import { useAuth } from '../../contexts/AuthContext';

const ReportTemplateModal = ({
  isOpen,
  onClose,
  template = null,
  onSave,
  clientId = null
}) => {
  const { t, i18n } = useTranslation();
  const { currentCompany, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [templateData, setTemplateData] = useState({
    name: '',
    description: '',
    type: 'shipments',
    fields: {},
    clientSpecific: false,
    clientId: null
  });

  useEffect(() => {
    if (template) {
      // Re-apply current-language defaults so old templates saved in another
      // language render with localized labels (and pick up any newly-added fields).
      setTemplateData(reportTemplatesService.applyLocalizedDefaults(template, t));
    } else {
      const defaultTemplate = reportTemplatesService.getDefaultTemplate(t);
      setTemplateData({
        ...defaultTemplate,
        name: '',
        description: '',
        clientSpecific: !!clientId,
        clientId: clientId,
        isDefault: false
      });
    }
  }, [template, clientId, i18n.language]);

  const handleSave = async () => {
    if (!templateData.name.trim()) {
      toast.error(t('reports.template_name_required', 'Template name is required'));
      return;
    }
    if (!currentCompany?.id) {
      toast.error(t('common.no_company_selected', 'No company selected'));
      return;
    }
    try {
      setLoading(true);
      const templateToSave = {
        ...templateData,
        companyId: currentCompany.id,
        createdBy: user?.uid
      };
      let templateId;
      if (template?.id) {
        await reportTemplatesService.updateTemplate(template.id, templateToSave);
        templateId = template.id;
        toast.success(t('reports.template_updated', 'Template updated successfully'));
      } else {
        templateId = await reportTemplatesService.createTemplate(templateToSave);
        toast.success(t('reports.template_created', 'Template created successfully'));
      }
      if (onSave) onSave({ ...templateToSave, id: templateId });
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(t('reports.template_save_error', 'Failed to save template'));
    } finally {
      setLoading(false);
    }
  };

  const toggleField = (fieldKey) => {
    setTemplateData(prev => ({
      ...prev,
      fields: {
        ...prev.fields,
        [fieldKey]: {
          ...prev.fields[fieldKey],
          enabled: !prev.fields[fieldKey]?.enabled
        }
      }
    }));
  };

  const updateFieldLabel = (fieldKey, label) => {
    setTemplateData(prev => ({
      ...prev,
      fields: {
        ...prev.fields,
        [fieldKey]: { ...prev.fields[fieldKey], label }
      }
    }));
  };

  const moveField = (fieldKey, direction) => {
    const sorted = Object.entries(templateData.fields).sort(
      ([, a], [, b]) => (a.order ?? 99) - (b.order ?? 99)
    );
    const currentIndex = sorted.findIndex(([key]) => key === fieldKey);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === sorted.length - 1)
    ) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const [moved] = sorted.splice(currentIndex, 1);
    sorted.splice(newIndex, 0, moved);

    const reorderedFields = {};
    sorted.forEach(([key, value], index) => {
      reorderedFields[key] = { ...value, order: index + 1 };
    });
    setTemplateData(prev => ({ ...prev, fields: reorderedFields }));
  };

  // All available field descriptions for the UI (labels/descriptions come from here)
  const availableFields = reportTemplatesService.getAvailableFields(t);
  const allFieldDescriptions = { ...availableFields.basic, ...availableFields.optional };

  // Sorted field list — uses templateData.fields order so up/down buttons take effect immediately
  const sortedFields = Object.entries(templateData.fields).sort(
    ([, a], [, b]) => (a.order ?? 99) - (b.order ?? 99)
  );

  const isRTL = i18n.language?.startsWith('ar');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <DocumentTextIcon className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {template
                ? t('reports.edit_template', 'Edit Report Template')
                : t('reports.create_template', 'Create Report Template')}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">

          {/* Template Info */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('reports.template_info', 'Template Information')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('reports.template_name', 'Template Name')} *
                </label>
                <input
                  type="text"
                  value={templateData.name}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                  className="input-field w-full"
                  placeholder={t('reports.enter_template_name', 'Enter template name')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('reports.template_type', 'Report Type')}
                </label>
                <select
                  value={templateData.type}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, type: e.target.value }))}
                  className="input-field w-full"
                  disabled
                >
                  <option value="shipments">{t('reports.shipments_report', 'Shipments Report')}</option>
                </select>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('reports.template_description', t('common.description'))}
              </label>
              <textarea
                value={templateData.description}
                onChange={(e) => setTemplateData(prev => ({ ...prev, description: e.target.value }))}
                className="input-field w-full"
                rows="2"
                placeholder={t('reports.enter_template_description', 'Enter template description')}
              />
            </div>
            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
              <input
                type="checkbox"
                id="clientSpecific"
                checked={templateData.clientSpecific}
                onChange={(e) => setTemplateData(prev => ({
                  ...prev,
                  clientSpecific: e.target.checked,
                  clientId: e.target.checked ? clientId : null
                }))}
              />
              <label htmlFor="clientSpecific" className="text-sm text-gray-700">
                {t('reports.client_specific_template', 'Client-specific template')}
              </label>
            </div>
          </div>

          {/* Field Configuration */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-gray-900">
                {t('reports.configure_fields', 'Configure Fields')}
              </h3>
              <span className="text-xs text-gray-500">
                {sortedFields.filter(([, f]) => f.enabled).length} {t('reports.fields_enabled', 'fields enabled')}
              </span>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm text-blue-700">
              {t('reports.field_config_help', 'Toggle fields on/off and use the arrows to reorder columns in the report.')}
            </div>

            {/* Unified sorted field list */}
            <div className="space-y-2">
              {sortedFields.map(([fieldKey, field], index) => {
                const info = allFieldDescriptions[fieldKey];
                const isFirst = index === 0;
                const isLast = index === sortedFields.length - 1;
                return (
                  <div
                    key={fieldKey}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      field.enabled
                        ? 'bg-white border-blue-200 shadow-sm'
                        : 'bg-gray-50 border-gray-200 opacity-70'
                    }`}
                  >
                    {/* Enable/disable toggle */}
                    <button
                      onClick={() => toggleField(fieldKey)}
                      className={`flex-shrink-0 p-1 rounded transition-colors ${
                        field.enabled ? 'text-green-600 hover:text-green-700' : 'text-gray-400 hover:text-gray-500'
                      }`}
                      title={field.enabled ? t('reports.disable_field', 'Disable field') : t('reports.enable_field', 'Enable field')}
                    >
                      {field.enabled
                        ? <EyeIcon className="w-5 h-5" />
                        : <EyeSlashIcon className="w-5 h-5" />}
                    </button>

                    {/* Label input + description */}
                    <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => updateFieldLabel(fieldKey, e.target.value)}
                        className="input-field w-full text-sm"
                        disabled={!field.enabled}
                        placeholder={info?.label || fieldKey}
                      />
                      <span className="text-xs text-gray-500 self-center truncate">
                        {info?.description || ''}
                      </span>
                    </div>

                    {/* Up / Down buttons */}
                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                      <button
                        onClick={() => moveField(fieldKey, 'up')}
                        disabled={isFirst}
                        className="p-1 rounded text-gray-400 hover:text-gray-700 disabled:opacity-20 disabled:cursor-not-allowed"
                        title={t('common.move_up', 'Move up')}
                      >
                        <ArrowUpIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveField(fieldKey, 'down')}
                        disabled={isLast}
                        className="p-1 rounded text-gray-400 hover:text-gray-700 disabled:opacity-20 disabled:cursor-not-allowed"
                        title={t('common.move_down', 'Move down')}
                      >
                        <ArrowDownIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button onClick={onClose} className="btn btn-secondary" disabled={loading}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button onClick={handleSave} className="btn btn-primary" disabled={loading}>
            {loading ? t('common.saving', 'Saving...') : t('common.save', 'Save Template')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportTemplateModal;
